import { useTranslations } from "next-intl";
import { useState, useEffect, useRef } from "react";
import { useReloadUserLists } from "@hooks/use-userlists";
import Input from "@components/core/Input";
import Button from "@components/core/Button";
import { postUserList, putUserList } from "@utils/call-api/userlists-call";
import { showErrorToast } from "@utils/toasts/showToasts";

function UserListForm({ onAfterSubmit = () => {}, initialUserList = {}, visible = true }) {
  const t = useTranslations("common");

  const reloadUserLists = useReloadUserLists();

  const [userListName, setUserListName] = useState(initialUserList.name || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const inputRef = useRef();

  function changeUserListName(e) {
    setUserListName(e.currentTarget.value);
  }

  async function createNewUserList() {
    const userList = await postUserList({ name: userListName });
    return userList;
  }

  async function updateUserList() {
    const userList = await putUserList(initialUserList.uuid, { name: userListName });
    return userList;
  }

  async function onSubmit(e) {
    e.preventDefault();

    if (loading) return;

    try {
      setLoading(true);
      setError("");
      let userList;

      if (initialUserList.uuid) {
        userList = await updateUserList();
      } else {
        userList = await createNewUserList();
      }

      reloadUserLists();

      onAfterSubmit(userList);
    } catch (err) {
      if (err.response?.data?.errors?.includes("name_blank")) {
        setError(t("userlist.errors.name_required"));
      } else {
        showErrorToast(
          t("userlist.errors.unknown.title"),
          t("userlist.errors.unknown.subtitle"),
        );
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    function focusOnInput() {
      setTimeout(() => {
        inputRef.current.focus();
      }, 400);
    }

    if (visible) focusOnInput();
  }, [visible]);

  return (
    <div>
      <h3 className="mb-2 text-center">{t("userlist.form.title")}</h3>
      <p className="mb-4 pb-2 text-center">{t("userlist.form.subtitle")}</p>

      <form onSubmit={onSubmit}>
        <Input
          ref={inputRef}
          type="text"
          label={t("userlist.form.placeholder")}
          placeholder={t("userlist.form.placeholder")}
          name="userListName"
          value={userListName}
          onChange={changeUserListName}
          required
          error={error}
        />

        <div className="d-flex justify-content-center mt-4 pt-2">
          <Button
            type="submit"
            variant="primary"
            hugContent
            loading={loading}
            trackingName="SaveUserlistFromOffcanvas"
          >
            {t("userlist.form.submit")}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default UserListForm;
